import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Divider,
  NumberFormatter,
  Skeleton,
  TextInput
} from '@mantine/core';
import { ChevronDown, ChevronRight, ChevronUp } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import PageView from '../../common/PageView';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';
import * as classes from '../../../styles/nestedStyles.module.css';
import { useAdminBackPath } from '../../../helpers/adminHelper';
import AppImage from '../../common/AppImage';
import PaginationList from '../../common/PaginationList';
import { triggerNotification } from '../../../helpers/notification';

const ESCROW_SORT_OPTIONS = [
  {
    label: 'Name',
    value: 'name',
    sort: (a, b) => a.ecomStore.name.localeCompare(b.ecomStore.name)
  },
  {
    label: 'Escrow balance',
    value: 'escrow_balance',
    sort: (a, b) => a.escrowBalance - b.escrowBalance
  }
];

const AdminStoreEscrowBalanceView = () => {
  const hasFetched = useRef(false);
  const { state, fetchAdminStoreEscrowOverview } = useContext(AdminContext);
  const { isDesktopOrSmaller } = useMediaQueryIndex();
  const { backPath } = useAdminBackPath('/admin/stores');
  const [viewState, setViewState] = useState({
    storeEscrowFilter: {
      sort: 'escrow_balance_desc',
      search: ''
    }
  });
  const escrowSortOption = ESCROW_SORT_OPTIONS.find(
    (o) => o.value === viewState.storeEscrowFilter.sort
  );
  const isDescending = viewState.storeEscrowFilter.sort.endsWith('_desc');

  const loading =
    !hasFetched.current ||
    state.ecomAdminStoreEscrowOverview.loading ||
    !state.ecomAdminStoreEscrowOverview.value;

  const storesWithEscrow =
    state.ecomAdminStoreEscrowOverview.value?.ecomStoreEscrow.filter(
      (s) => s.escrowBalance > 0
    ) || [];
  const storesWithEscrowFiltered = storesWithEscrow.filter(
    (e) =>
      !viewState.storeEscrowFilter.search ||
      e.ecomStore.name
        .toLowerCase()
        .includes(viewState.storeEscrowFilter.search.toLowerCase())
  );

  const totalEscrowValues = state.ecomAdminStoreEscrowOverview.value?.ecomStoreEscrow.reduce(
    (acc, curr) => {
      acc.escrowBalance += curr.escrowBalance;
      acc.escrowClaimed += curr.escrowClaimed;
      return acc;
    },
    { escrowBalance: 0, escrowClaimed: 0 }
  ) || { escrowBalance: 0, escrowClaimed: 0 };

  const escrowPayouts =
    state.ecomAdminStoreEscrowOverview.value?.escrowPayouts || [];

  useEffect(() => {
    if (!state.ecomAdminStoreEscrowOverview.value) {
      fetchAdminStoreEscrowOverview({}, null, triggerNotification);
    }
    hasFetched.current = true;
  }, []);

  const onEscrowFilterSortChange = (newSort) => {
    const newEscrowSort = ESCROW_SORT_OPTIONS.find((o) => o.value === newSort);
    if (newEscrowSort) {
      setViewState((prevState) => ({
        ...prevState,
        storeEscrowFilter: {
          ...prevState.storeEscrowFilter,
          sort:
            prevState.storeEscrowFilter.sort === newSort
              ? `${newSort}_desc`
              : newSort
        }
      }));
    }
  };

  return (
    <PageView backPath={backPath} title="Store escrow">
      <AppFlexbox
        style={{
          flexDirection: isDesktopOrSmaller ? 'column-reverse' : 'row',
          width: '100%'
        }}
      >
        <AppStack style={{ flex: 1, width: '100%' }}>
          <AppCard radius="md" shadow="xs" style={{ padding: 0 }} withBorder>
            <AppStack style={{ gap: 0 }}>
              <AppFlexbox
                style={{ padding: '8px 16px', gap: 8, alignItems: 'center' }}
              >
                <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                  Stores with escrow
                </AppText>
                {!loading && storesWithEscrow.length > 0 && (
                  <Badge circle>{storesWithEscrow.length}</Badge>
                )}
              </AppFlexbox>
              <TextInput
                onChange={(e) =>
                  setViewState({
                    ...viewState,
                    storeEscrowFilter: {
                      ...viewState.storeEscrowFilter,
                      search: e.currentTarget.value
                    }
                  })
                }
                placeholder="Search store by name"
                radius={0}
                size="sm"
                value={viewState.storeEscrowFilter.search}
              />
              <AppFlexbox
                style={{
                  backgroundColor: '#f1f1f1',
                  padding: '8px 16px',
                  gap: 8
                }}
              >
                <AppFlexbox style={{ flex: 1 }}>
                  <AppFlexbox
                    onClick={() => {
                      onEscrowFilterSortChange('name');
                    }}
                    style={{
                      cursor: 'pointer',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Name
                    </AppText>

                    {viewState.storeEscrowFilter.sort === 'name' ? (
                      <ChevronUp color="#666" size={18} />
                    ) : viewState.storeEscrowFilter.sort === 'name_desc' ? (
                      <ChevronDown color="#666" size={18} />
                    ) : null}
                  </AppFlexbox>
                </AppFlexbox>
                <AppFlexbox
                  onClick={() => {
                    onEscrowFilterSortChange('escrow_balance');
                  }}
                  style={{
                    minWidth: 75,
                    cursor: 'pointer',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Balance
                  </AppText>

                  {viewState.storeEscrowFilter.sort === 'escrow_balance' ? (
                    <ChevronUp color="#666" size={18} />
                  ) : viewState.storeEscrowFilter.sort ===
                    'escrow_balance_desc' ? (
                    <ChevronDown color="#666" size={18} />
                  ) : null}
                </AppFlexbox>
              </AppFlexbox>
              {loading ? (
                Array.from(Array(5)).map((x, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={i}>
                    <Divider />
                    <AppFlexbox
                      style={{
                        padding: 16,
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <AppFlexbox
                        style={{ flex: 1, gap: 8, alignItems: 'center' }}
                      >
                        <Skeleton height={32} width={32} />
                        <AppStack style={{ gap: 0 }}>
                          <Skeleton height={18} width={200} />
                        </AppStack>
                      </AppFlexbox>

                      <AppFlexbox style={{ alignItems: 'center', gap: 8 }}>
                        <Skeleton height={18} width={100} />
                      </AppFlexbox>
                    </AppFlexbox>
                  </React.Fragment>
                ))
              ) : storesWithEscrowFiltered.length === 0 ? (
                <AppStack
                  style={{
                    padding: 30,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  <AppText
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#666',
                      textAlign: 'center'
                    }}
                  >
                    {storesWithEscrow.length === 0
                      ? 'No stores with escrow found'
                      : 'No stores found by search'}
                  </AppText>

                  {storesWithEscrow.length > 0 && (
                    <Button
                      color="dark"
                      onClick={() => {
                        setViewState({
                          ...viewState,
                          storeEscrowFilter: {
                            ...viewState.storeEscrowFilter,
                            search: ''
                          }
                        });
                      }}
                      size="compact-md"
                      variant="outline"
                    >
                      Clear search
                    </Button>
                  )}
                </AppStack>
              ) : (
                <AppStack
                  style={{
                    gap: 0,
                    paddingBottom: storesWithEscrowFiltered.length > 10 ? 8 : 0
                  }}
                >
                  <Divider />
                  <PaginationList
                    items={storesWithEscrowFiltered
                      .sort(
                        (a, b) =>
                          escrowSortOption?.sort(
                            isDescending ? b : a,
                            isDescending ? a : b
                          ) || b.escrowBalance - a.escrowBalance
                      )
                      .map((e, index) => (
                        <AppStack
                          key={e.ecomStore.pkEcomStore}
                          style={{ gap: 0 }}
                        >
                          <AppFlexbox
                            className={classes['hover-action-card']}
                            component={Link}
                            style={{
                              padding: 16,
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              textDecoration: 'none',
                              color: '#000'
                            }}
                            to={`/admin/stores/${e.ecomStore.pkEcomStore}/escrow/create-payout?from=escrow-balance`}
                          >
                            <AppFlexbox
                              style={{ flex: 1, gap: 8, alignItems: 'center' }}
                            >
                              <AppStack style={{ width: 32, height: 32 }}>
                                <AppImage src={e.ecomStore.logoImageUrl} />
                              </AppStack>
                              <AppStack style={{ gap: 0 }}>
                                <AppText
                                  style={{ fontSize: 14, fontWeight: 500 }}
                                >
                                  {e.ecomStore.name}
                                </AppText>
                              </AppStack>
                            </AppFlexbox>

                            <AppFlexbox
                              style={{ alignItems: 'center', gap: 8 }}
                            >
                              <AppStack>
                                <NumberFormatter
                                  decimalScale={2}
                                  fixedDecimalScale
                                  prefix="$"
                                  style={{ fontSize: 14 }}
                                  thousandSeparator
                                  value={e.escrowBalance / 100}
                                />
                              </AppStack>
                              <ChevronRight color="#666" size={18} />
                            </AppFlexbox>
                          </AppFlexbox>
                          {(storesWithEscrowFiltered.length > 10 ||
                            index !== storesWithEscrowFiltered.length - 1) && (
                            <Divider />
                          )}
                        </AppStack>
                      ))}
                    itemsPerPage={10}
                    noBorder
                  />
                </AppStack>
              )}
            </AppStack>
          </AppCard>
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isDesktopOrSmaller ? 'unset' : 400 }}
        >
          <AppCard radius="md" shadow="xs" style={{ padding: 0 }} withBorder>
            <AppStack style={{ gap: 0 }}>
              <AppStack style={{ padding: 16, gap: 8 }}>
                <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                  Escrow balance
                </AppText>

                {loading ? (
                  <Skeleton
                    height={24}
                    style={{ margin: '5px 0px' }}
                    width={100}
                  />
                ) : (
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 22 }}
                    thousandSeparator
                    value={totalEscrowValues.escrowBalance / 100}
                  />
                )}
              </AppStack>
              <Divider />
              <AppStack style={{ padding: 16, gap: 8 }}>
                <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                  Escrow paid out
                </AppText>
                {loading ? (
                  <Skeleton
                    height={24}
                    style={{ margin: '5px 0px' }}
                    width={100}
                  />
                ) : (
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 22 }}
                    thousandSeparator
                    value={totalEscrowValues.escrowClaimed / 100}
                  />
                )}
              </AppStack>
              <Divider />
              <AppStack style={{ padding: 16, gap: 8 }}>
                <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                  Total escrow held
                </AppText>
                {loading ? (
                  <Skeleton
                    height={24}
                    style={{ margin: '5px 0px' }}
                    width={100}
                  />
                ) : (
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontSize: 22 }}
                    thousandSeparator
                    value={
                      (totalEscrowValues.escrowBalance +
                        totalEscrowValues.escrowClaimed) /
                      100
                    }
                  />
                )}
              </AppStack>
            </AppStack>
          </AppCard>
          <AppCard radius="md" shadow="xs" style={{ padding: 0 }} withBorder>
            <AppStack style={{ gap: 0 }}>
              <AppFlexbox
                style={{ padding: '8px 16px', gap: 8, alignItems: 'center' }}
              >
                <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                  Payouts
                </AppText>
                {!loading && <Badge circle>{escrowPayouts.length}</Badge>}
              </AppFlexbox>
              {loading ? (
                Array.from(Array(5)).map((x, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={i}>
                    <Divider />
                    <AppFlexbox
                      style={{
                        padding: 16,
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <AppStack style={{ gap: 5 }}>
                        <Skeleton height={18} width={200} />
                        <Skeleton height={18} width={200} />
                      </AppStack>
                    </AppFlexbox>
                  </React.Fragment>
                ))
              ) : escrowPayouts.length === 0 ? (
                <>
                  <Divider />
                  <AppFlexbox style={{ padding: 16, justifyContent: 'center' }}>
                    <AppText
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#666',
                        textAlign: 'center'
                      }}
                    >
                      No payouts found
                    </AppText>
                  </AppFlexbox>
                </>
              ) : (
                <AppStack
                  style={{
                    gap: 0,
                    paddingBottom: storesWithEscrowFiltered.length > 10 ? 8 : 0
                  }}
                >
                  <Divider />
                  <PaginationList
                    items={escrowPayouts
                      .sort(
                        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
                      )
                      .map((p, index) => (
                        <AppStack
                          key={`${p.ecomStore.pkEcomStore}_${p.transferId}`}
                          style={{ gap: 0 }}
                        >
                          <AppFlexbox
                            style={{
                              padding: 16,
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              textDecoration: 'none',
                              color: '#000'
                            }}
                          >
                            <AppStack style={{ gap: 0 }}>
                              <NumberFormatter
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$"
                                style={{ fontSize: 14, fontWeight: 500 }}
                                thousandSeparator
                                value={(p.amountPaid + p.amountReversed) / 100}
                              />
                              <AppText style={{ fontSize: 14, color: '#666' }}>
                                {p.ecomStore.name}
                              </AppText>
                            </AppStack>
                          </AppFlexbox>
                          {(escrowPayouts.length > 10 ||
                            index !== escrowPayouts.length - 1) && <Divider />}
                        </AppStack>
                      ))}
                    itemsPerPage={10}
                    noBorder
                  />
                </AppStack>
              )}
            </AppStack>
          </AppCard>
        </AppStack>
      </AppFlexbox>
    </PageView>
  );
};

export default AdminStoreEscrowBalanceView;
